import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { StockAllocationResult, StockAllocationType } from '@asm/features/quick-order/models/stock-allocation.model';
import { StockDetailsDTO } from '@asm/features/quick-order/models/stock-details.model';
import { StockLevelRepository } from '@asm/features/sync/repositories/stock-level.repository';
import { FutureStockRepository } from '@asm/features/sync/repositories/future-stock.repository';
import { QuotaRepository } from '@asm/features/sync/repositories/quota.repository';

@Injectable({ providedIn: 'root' })
export class DefaultLocalStockService {
  private readonly stockLevelRepo = inject(StockLevelRepository);
  private readonly futureStockRepo = inject(FutureStockRepository);
  private readonly quotaRepo = inject(QuotaRepository);

  async getImmediateStockAvailable(productCode: string, warehouseCode: string): Promise<number> {
    const all = await firstValueFrom(this.stockLevelRepo.getAll());
    const stock = all.find(item => item.productCode === productCode && item.warehouseCode === warehouseCode);

    if (!stock) {
      return 0;
    }

    return Math.max(0, Number(stock.available) - Number(stock.reserved));
  }

  async getFutureStockBatches(
    productCode: string,
    warehouseCode: string,
    type: 'FUTURE' | 'TRANSPORT',
  ): Promise<{ date: Date; available: number }[]> {
    const all = await firstValueFrom(this.futureStockRepo.getAll());
    return all
      .filter(item => item.productCode === productCode && item.warehouseCode === warehouseCode && item.type === type)
      .map(item => ({
        date: new Date(item.date),
        available: Math.max(0, Number(item.available) - Number(item.reserved)),
      }))
      .sort((left, right) => left.date.getTime() - right.date.getTime());
  }

  async allocateForDistribution(
    productCode: string,
    quantity: number,
    warehouseCode: string,
  ): Promise<StockAllocationResult> {
    const result: StockAllocationResult = {
      totalRequested: quantity,
      totalAllocated: 0,
      isFullyAllocated: false,
      allocations: [],
    };

    let remaining = quantity;
    const immediate = await this.getImmediateStockAvailable(productCode, warehouseCode);

    if (immediate > 0 && remaining > 0) {
      const fromImmediate = Math.min(immediate, remaining);
      result.allocations.push({ type: StockAllocationType.IMMEDIATE, quantity: fromImmediate });
      remaining -= fromImmediate;
    }

    if (remaining > 0) {
      const transportBatches = await this.getFutureStockBatches(productCode, warehouseCode, 'TRANSPORT');

      for (const batch of transportBatches) {
        if (batch.available <= 0 || remaining <= 0) {
          continue;
        }

        const fromBatch = Math.min(batch.available, remaining);
        result.allocations.push({
          type: StockAllocationType.TRANSPORT,
          quantity: fromBatch,
          date: batch.date.toISOString(),
        });
        remaining -= fromBatch;

        if (remaining === 0) {
          break;
        }
      }
    }

    result.totalAllocated = quantity - remaining;
    result.isFullyAllocated = remaining === 0;

    if (result.totalAllocated === 0) {
      result.message = 'Produto indisponivel';
    } else if (!result.isFullyAllocated) {
      result.message = `A quantidade foi ajustada para o valor disponivel: ${result.totalAllocated}`;
    }

    return result;
  }

  async allocateForFrigorifico(
    productCode: string,
    quantity: number,
    warehouseCode: string,
    b2bUnitId: string,
    requesterId?: string,
  ): Promise<StockAllocationResult> {
    const result: StockAllocationResult = {
      totalRequested: quantity,
      totalAllocated: 0,
      isFullyAllocated: false,
      allocations: [],
    };

    let remaining = quantity;
    const immediate = await this.getImmediateStockAvailable(productCode, warehouseCode);

    if (immediate > 0 && remaining > 0) {
      const fromImmediate = Math.min(immediate, remaining);
      result.allocations.push({ type: StockAllocationType.IMMEDIATE, quantity: fromImmediate });
      remaining -= fromImmediate;
    }

    if (remaining > 0) {
      const futureBatches = await this.getFutureStockBatches(productCode, warehouseCode, 'FUTURE');

      for (const batch of futureBatches) {
        if (batch.available <= 0 || remaining <= 0) {
          continue;
        }

        const fromBatch = Math.min(batch.available, remaining);
        result.allocations.push({
          type: StockAllocationType.FUTURE,
          quantity: fromBatch,
          date: batch.date.toISOString(),
        });
        remaining -= fromBatch;

        if (remaining === 0) {
          break;
        }
      }
    }

    if (remaining > 0) {
      const quotas = await firstValueFrom(this.quotaRepo.getAll());
      const availableQuotas = quotas.filter(item => {
        const sameProduct = item.productId === productCode;
        const sameUnit = item.b2bUnitId === b2bUnitId;
        const sameRequester = requesterId ? item.requesterId === requesterId : true;
        return sameProduct && sameUnit && sameRequester && item.status === 'OPEN';
      });

      for (const quota of availableQuotas) {
        const available = Math.max(0, Number(quota.balance) - Number(quota.consumedQty));

        if (available <= 0 || remaining <= 0) {
          continue;
        }

        const fromQuota = Math.min(available, remaining);
        result.allocations.push({
          type: StockAllocationType.QUOTA,
          quantity: fromQuota,
          quotaId: quota.id,
          releaseDate: new Date(quota.desiredDate).toISOString(),
        });
        remaining -= fromQuota;

        if (remaining === 0) {
          break;
        }
      }
    }

    result.totalAllocated = quantity - remaining;
    result.isFullyAllocated = remaining === 0;

    if (result.totalAllocated === 0) {
      result.message = 'Produto indisponivel';
    } else if (!result.isFullyAllocated) {
      result.message = `A quantidade foi ajustada para o valor disponivel: ${result.totalAllocated}`;
    }

    return result;
  }

  async getStockDetails(
    productCode: string,
    warehouseCode: string,
    b2bUnitId: string,
    distributionChannel: string,
    requesterId?: string,
  ): Promise<StockDetailsDTO> {
    const result: StockDetailsDTO = {
      productCode,
      warehouseCode,
      immediate: { available: 0 },
      quotas: [],
      future: [],
      transport: [],
    };

    result.immediate.available = await this.getImmediateStockAvailable(productCode, warehouseCode);

    if (distributionChannel === '10') {
      const quotas = await firstValueFrom(this.quotaRepo.getAll());
      result.quotas = quotas
        .filter(item => {
          const sameProduct = item.productId === productCode;
          const sameUnit = item.b2bUnitId === b2bUnitId;
          const sameRequester = requesterId ? item.requesterId === requesterId : true;
          return sameProduct && sameUnit && sameRequester && item.status === 'OPEN';
        })
        .map(item => ({
          quotaId: item.id,
          releaseDate: new Date(item.desiredDate).toISOString(),
          available: Math.max(0, Number(item.balance) - Number(item.consumedQty)),
          b2bUnitName: '',
        }));
      result.future = this.groupBatchesByMonth(await this.getFutureStockBatches(productCode, warehouseCode, 'FUTURE'));
    }

    if (distributionChannel === '20') {
      result.transport = this.groupBatchesByMonth(
        await this.getFutureStockBatches(productCode, warehouseCode, 'TRANSPORT'),
      );
    }

    return result;
  }

  private groupBatchesByMonth(
    batches: { date: Date; available: number }[],
  ): { month: string; batches: { date: string; available: number }[] }[] {
    const grouped = new Map<string, { date: string; available: number }[]>();

    for (const batch of batches) {
      const month = String(batch.date.getMonth() + 1).padStart(2, '0');
      const key = `${month}/${batch.date.getFullYear()}`;
      const current = grouped.get(key) ?? [];
      current.push({ date: batch.date.toISOString(), available: batch.available });
      grouped.set(key, current);
    }

    return Array.from(grouped.entries()).map(([month, monthBatches]) => ({
      month,
      batches: monthBatches.sort((left, right) => new Date(left.date).getTime() - new Date(right.date).getTime()),
    }));
  }
}
